<template>
    <div class="flex justify-center">
        <RedesignBasePopover
            :disabled="!sortedQueueCalls.length"
        >
            <template #reference>
                <span>{{ maxWaitTime }}</span>
            </template>
            <div class="p-1">
                <template v-if="isAllQueueRow">
                    <div
                        v-for="queue in sortedQueueCallsGroupedByQueueID"
                        :key="queue.QueueID"
                    >
                        <p class="font-semibold text-sm">
                            {{ $t('widget.queue.queueId') }}: {{ queue.QueueName }}
                            <span class="font-normal">({{ queue.QueueID }})</span>
                        </p>
                        <div
                            v-for="(queueCall, index) in queue.Calls"
                            :key="queueCall.IvrUniqueID"
                            class="ml-2 my-1 text-xs"
                        >
                            <p>
                                {{ $t('widget.caller.id') }}: {{ queueCall.CallerID }}
                            </p>
                            <p>
                                {{ formatDate(queueCall.JoinTimeStamp_UTC_CLIENT) }}
                            </p>

                            <hr v-if="index !== queue.Calls.length - 1">
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-for="(queueCall, index) in sortedQueueCalls"
                        :key="queueCall.IvrUniqueID"
                        class="text-xs my-1"
                    >
                        <p>
                            {{ $t('widget.caller.id') }}: {{ queueCall.CallerID }}
                        </p>
                        <p>
                            {{ formatDate(queueCall.JoinTimeStamp_UTC_CLIENT) }}
                        </p>

                        <hr v-if="index !== sortedQueueCalls.length - 1">
                    </div>
                </template>
            </div>
        </RedesignBasePopover>
        <RedesignBasePopover
            v-if="joinTimestampAndVHJoinTimestampDiff"
        >
            <template #reference>
                <span>({{ joinTimestampAndVHJoinTimestampDiff }})</span>
            </template>
            <span class="p-2 text-sm">
                {{ $t('widget.virtualHoldWaitTimeDescription') }}
            </span>
        </RedesignBasePopover>
    </div>
</template>

<script>
import get from 'lodash/get'
import { dayjs } from '@/composables/useDayjs'
import { getInitialTime } from '@/util/timeUtils'
import Timer from '@/util/Timer'
import { ALL_QUEUE_ID } from '@/enum/redesignQueueDashboardStatistics'
import { timeFormatter } from '@/helpers/timeFormatter.js'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'

export default {
    components: { RedesignBasePopover },
    props: {
        queueID: {
            type: [ Number, String ],
            default: null
        },
        queueName: {
            type: String
        }
    },
    data () {
        return {
            timeout: null,
            timer: new Timer(),
            joinTimestampAndVHJoinTimestampDiff: 0
        }
    },
    computed: {
        getQueueByID () {
            return this.$store.getters['queues/filterQueuesByIds']([ this.queueID ])
        },
        allQueueCalls () {
            return this.$store.getters['queues/allQueueCalls']
        },
        maxWaitTime () {
            return this.timer.displayTime
        },
        isAllQueueRow () {
            return this.queueID === ALL_QUEUE_ID
        },
        queueCalls () {
            let calls = []

            if (this.isAllQueueRow) {
                calls = this.allQueueCalls
            } else {
                calls = get(this.getQueueByID, '[0].Calls', [])
            }
            return calls
        },
        sortedQueueCalls () {
            return [ ...this.queueCalls ].sort((a, b) =>
                a.JoinTimeStamp_UTC_CLIENT - b.JoinTimeStamp_UTC_CLIENT
            )
        },
        sortedQueueCallsGroupedByQueueID () {
            if (!this.isAllQueueRow) {
                return []
            }

            const groupedCalls = this.allQueueCalls.reduce(
                (acc, call) => {
                    if (!acc[call.QueueID]) {
                        acc[call.QueueID] = {
                            QueueName: this.$store.getters['queues/filterQueuesByIds']([ call.QueueID ])[0].QueueName,
                            QueueID: call.QueueID,
                            Calls: [],
                            minJoinTimestamp: Infinity
                        }
                    }

                    acc[call.QueueID].Calls.push(call)
                    acc[call.QueueID].minJoinTimestamp = Math.min(acc[call.QueueID].minJoinTimestamp, call.JoinTimeStamp_UTC_CLIENT)

                    return acc
                },
                {}
            )

            return Object.values(groupedCalls)
                .sort((a, b) => a.minJoinTimestamp - b.minJoinTimestamp)
                .map(queue => ({
                    ...queue,
                    Calls: queue.Calls.sort((a, b) => a.JoinTimeStamp_UTC_CLIENT - b.JoinTimeStamp_UTC_CLIENT)
                }))
        }
    },
    methods: {
        formatDate (timestamp) {
            return dayjs(new Date(timestamp)).format('YY-MM-DD HH:mm')
        },
        getStats () {
            const calls = this.queueCalls

            let minJoinTimestamp = 0
            let joinTimestampAndVHJoinTimestampDiff = 0
            let callWithMinJoinTimestamp

            if (calls.length) {
                // Find the call with minimum join timestamp, which means the call that has been waiting the longest
                callWithMinJoinTimestamp = calls.reduce(
                    (minCall, call) => {
                        if (call.JoinTimeStamp_UTC_CLIENT < minCall.JoinTimeStamp_UTC_CLIENT) {
                            return call
                        }
                        return minCall
                    },
                    calls[0]
                )
            }

            if (callWithMinJoinTimestamp && callWithMinJoinTimestamp.VHJoinTimeStamp_UTC_CLIENT) {
                joinTimestampAndVHJoinTimestampDiff = timeFormatter((callWithMinJoinTimestamp.VHJoinTimeStamp_UTC_CLIENT / 1000) - (callWithMinJoinTimestamp.JoinTimeStamp_UTC_CLIENT / 1000))
            }

            if (callWithMinJoinTimestamp) {
                minJoinTimestamp = getInitialTime(callWithMinJoinTimestamp.JoinTimeStamp_UTC_CLIENT)
            }

            return {
                minJoinTimestamp,
                joinTimestampAndVHJoinTimestampDiff,
                calls
            }
        },
        setCounterState (calls) {
            const stats = this.getStats()
            this.timer.stop()
            this.timer.setValue(stats.minJoinTimestamp)

            this.joinTimestampAndVHJoinTimestampDiff = stats.joinTimestampAndVHJoinTimestampDiff

            if (calls.length !== 0) {
                this.timer.start()
            }
        }
    },
    watch: {
        queueCalls: {
            deep: true,
            immediate: true,
            handler (calls) {
                this.setCounterState(calls)
            }
        },
        maxWaitTime: {
            immediate: true,
            handler (val) {
                if (!this.isAllQueueRow) {
                    this.$emit('getMaxWaitTime', {
                        value: val,
                        queueID: this.queueID,
                        queueName: this.queueName
                    })
                }
            }
        }
    },
    beforeUnmount () {
        this.timer.destroy()
    },
}
</script>

